import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { CashForecastService } from '../../services/cashForecast.service';
import { ForecastSettingsRequest, OverdueArHandling } from '../../interfaces/cashForecast.interface';

interface Props { institutionId: string; canManage: boolean; onClose: () => void; onSaved: () => void }

const FIELD = { flex: '1 1 220px', maxWidth: 320 } as const;

/** The forecast's assumptions. Defaults apply until a row is saved; edits need ACCOUNT_MANAGE. */
const ForecastSettingsDialog: React.FC<Props> = ({ institutionId, canManage, onClose, onSaved }) => {
  const toast = useToast();
  const [form, setForm] = useState<ForecastSettingsRequest | null>(null);
  const [saved, setSaved] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    CashForecastService.getSettings(institutionId)
      .then(s => {
        if (!live) return;
        setSaved(s.saved);
        setForm({
          exportRealizationDays: s.exportRealizationDays, payrollPayDay: s.payrollPayDay,
          withholdingDepositDay: s.withholdingDepositDay, poPaymentLagDays: s.poPaymentLagDays,
          overdueArHandling: s.overdueArHandling, minimumCash: s.minimumCash,
          overdueArCutoffDays: s.overdueArCutoffDays, overdueApCutoffDays: s.overdueApCutoffDays,
        });
      })
      .catch(err => { toast.showApiError(err, 'Could not load the forecast assumptions'); onClose(); });
    return () => { live = false; };
  }, [institutionId, toast, onClose]);

  const num = (key: keyof ForecastSettingsRequest) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => (f ? { ...f, [key]: e.target.value === '' ? ('' as unknown as number) : Number(e.target.value) } : f));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await CashForecastService.saveSettings(institutionId, form);
      toast.showSuccess('Forecast assumptions saved');
      onSaved();
    } catch (err) {
      toast.showApiError(err, 'Could not save the forecast assumptions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog data-help-page="cashForecast.settings.page" open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Forecast assumptions</DialogTitle>
      <DialogContent dividers>
        {!form ? <CircularProgress size={24} /> : (
          <>
            {!saved && <Alert severity="info" sx={{ mb: 2 }}>Showing the defaults. Save to make them yours.</Alert>}
            <Box data-help-section="cashForecast.settings.section.assumptions" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField data-help="cashForecast.settings.field.exportRealisationDays" sx={FIELD} size="small" type="number" label="Export realisation (days after EXP date)" disabled={!canManage}
                value={form.exportRealizationDays} onChange={num('exportRealizationDays')} inputProps={{ min: 0, max: 730 }} />
              <TextField data-help="cashForecast.settings.field.payrollPayDay" sx={FIELD} size="small" type="number" label="Payroll pay day (of the month)" disabled={!canManage}
                value={form.payrollPayDay} onChange={num('payrollPayDay')} inputProps={{ min: 1, max: 28 }} />
              <TextField data-help="cashForecast.settings.field.depositDay" sx={FIELD} size="small" type="number" label="VDS / TDS deposit day (of the month)" disabled={!canManage}
                value={form.withholdingDepositDay} onChange={num('withholdingDepositDay')} inputProps={{ min: 1, max: 28 }} />
              <TextField data-help="cashForecast.settings.field.poLagDays" sx={FIELD} size="small" type="number" label="Purchase order payment lag (days)" disabled={!canManage}
                value={form.poPaymentLagDays} onChange={num('poPaymentLagDays')} inputProps={{ min: 0, max: 365 }} />
              <TextField data-help="cashForecast.settings.field.overdueHandling" sx={FIELD} size="small" select label="Overdue customer invoices" disabled={!canManage}
                value={form.overdueArHandling}
                onChange={e => setForm(f => (f ? { ...f, overdueArHandling: e.target.value as OverdueArHandling } : f))}>
                <MenuItem value="WEEK_1">Collect in week 1</MenuItem>
                <MenuItem value="EXCLUDE">Leave out</MenuItem>
              </TextField>
              <TextField data-help="cashForecast.settings.field.arCutoffDays" sx={FIELD} size="small" type="number" label="Leave out overdue invoices older than (days)" disabled={!canManage}
                value={form.overdueArCutoffDays} onChange={num('overdueArCutoffDays')} inputProps={{ min: 0, max: 3650 }}
                helperText="0 = never leave out" />
              <TextField data-help="cashForecast.settings.field.apCutoffDays" sx={FIELD} size="small" type="number" label="Leave out overdue bills older than (days)" disabled={!canManage}
                value={form.overdueApCutoffDays} onChange={num('overdueApCutoffDays')} inputProps={{ min: 0, max: 3650 }}
                helperText="0 = never leave out" />
              <TextField data-help="cashForecast.settings.field.minimumCash" sx={FIELD} size="small" type="number" label="Minimum cash (BDT)" disabled={!canManage}
                value={form.minimumCash} onChange={num('minimumCash')} inputProps={{ min: 0, step: '0.01' }}
                helperText="Weeks closing below this are highlighted" />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button data-help="cashForecast.settings.cancel" onClick={onClose}>{canManage ? 'Cancel' : 'Close'}</Button>
        {canManage && <Button data-help="cashForecast.settings.save" variant="contained" onClick={save} disabled={!form || saving}>Save</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default ForecastSettingsDialog;
